/**
 * Eldritch Invocations for Warlocks (SRD)
 *
 * Warlocks gain Eldritch Invocations at level 2 and can learn additional
 * invocations at higher levels. Some invocations have prerequisites.
 */

export interface InvocationPrerequisite {
  type: 'level' | 'pact' | 'spell' | 'cantrip';
  value: string | number;
}

export interface EldritchInvocation {
  id: string;
  name: string;
  description: string;
  prerequisites?: InvocationPrerequisite[];
}

export const ELDRITCH_INVOCATIONS: EldritchInvocation[] = [
  {
    id: 'agonizing-blast',
    name: 'Agonizing Blast',
    description:
      'When you cast eldritch blast, add your Charisma modifier to the damage it deals on a hit.',
    prerequisites: [{ type: 'cantrip', value: 'eldritch-blast' }],
  },
  {
    id: 'armor-of-shadows',
    name: 'Armor of Shadows',
    description: 'You can cast mage armor on yourself at will, without expending a spell slot or material components.',
  },
  {
    id: 'ascendant-step',
    name: 'Ascendant Step',
    description:
      'You can cast levitate on yourself at will, without expending a spell slot or material components.',
    prerequisites: [{ type: 'level', value: 9 }],
  },
  {
    id: 'beast-speech',
    name: 'Beast Speech',
    description: 'You can cast speak with animals at will, without expending a spell slot.',
  },
  {
    id: 'beguiling-influence',
    name: 'Beguiling Influence',
    description: 'You gain proficiency in the Deception and Persuasion skills.',
  },
  {
    id: 'bewitching-whispers',
    name: 'Bewitching Whispers',
    description:
      'You can cast compulsion once using a warlock spell slot. You can\'t do so again until you finish a long rest.',
    prerequisites: [{ type: 'level', value: 7 }],
  },
  {
    id: 'book-of-ancient-secrets',
    name: 'Book of Ancient Secrets',
    description:
      'You can now inscribe magical rituals in your Book of Shadows. Choose two 1st-level spells that have the ritual tag from any class\'s spell list. The spells appear in the book and don\'t count against the number of spells you know. With your Book of Shadows in hand, you can cast the chosen spells as rituals. You can\'t cast the spells except as rituals, unless you\'ve learned them by some other means. You can also cast a warlock spell you know as a ritual if it has the ritual tag. On your adventures, you can add other ritual spells to your Book of Shadows. When you find such a spell, you can add it to the book if the spell\'s level is equal to or less than half your warlock level (rounded up) and if you can spare the time to transcribe the spell. For each level of the spell, the transcription process takes 2 hours and costs 50 gp for the rare inks needed to inscribe it.',
    prerequisites: [{ type: 'pact', value: 'pact-of-the-tome' }],
  },
  {
    id: 'chains-of-carceri',
    name: 'Chains of Carceri',
    description:
      'You can cast hold monster at will—targeting a celestial, fiend, or elemental—without expending a spell slot or material components. You must finish a long rest before you can use this invocation on the same creature again.',
    prerequisites: [
      { type: 'level', value: 15 },
      { type: 'pact', value: 'pact-of-the-chain' },
    ],
  },
  {
    id: 'devils-sight',
    name: "Devil's Sight",
    description:
      'You can see normally in darkness, both magical and nonmagical, to a distance of 120 feet.',
  },
  {
    id: 'dreadful-word',
    name: 'Dreadful Word',
    description:
      'You can cast confusion once using a warlock spell slot. You can\'t do so again until you finish a long rest.',
    prerequisites: [{ type: 'level', value: 7 }],
  },
  {
    id: 'eldritch-sight',
    name: 'Eldritch Sight',
    description: 'You can cast detect magic at will, without expending a spell slot.',
  },
  {
    id: 'eldritch-spear',
    name: 'Eldritch Spear',
    description: 'When you cast eldritch blast, its range is 300 feet.',
    prerequisites: [{ type: 'cantrip', value: 'eldritch-blast' }],
  },
  {
    id: 'eyes-of-the-rune-keeper',
    name: 'Eyes of the Rune Keeper',
    description: 'You can read all writing.',
  },
  {
    id: 'fiendish-vigor',
    name: 'Fiendish Vigor',
    description:
      'You can cast false life on yourself at will as a 1st-level spell, without expending a spell slot or material components.',
  },
  {
    id: 'gaze-of-two-minds',
    name: 'Gaze of Two Minds',
    description:
      'You can use your action to touch a willing humanoid and perceive through its senses until the end of your next turn. As long as the creature is on the same plane of existence as you, you can use your action on subsequent turns to maintain this connection, extending the duration until the end of your next turn. While perceiving through the other creature\'s senses, you benefit from any special senses possessed by that creature, and you are blinded and deafened to your own surroundings.',
  },
  {
    id: 'lifedrinker',
    name: 'Lifedrinker',
    description:
      'When you hit a creature with your pact weapon, the creature takes extra necrotic damage equal to your Charisma modifier (minimum 1).',
    prerequisites: [
      { type: 'level', value: 12 },
      { type: 'pact', value: 'pact-of-the-blade' },
    ],
  },
  {
    id: 'mask-of-many-faces',
    name: 'Mask of Many Faces',
    description: 'You can cast disguise self at will, without expending a spell slot.',
  },
  {
    id: 'master-of-myriad-forms',
    name: 'Master of Myriad Forms',
    description: 'You can cast alter self at will, without expending a spell slot.',
    prerequisites: [{ type: 'level', value: 15 }],
  },
  {
    id: 'minions-of-chaos',
    name: 'Minions of Chaos',
    description:
      'You can cast conjure elemental once using a warlock spell slot. You can\'t do so again until you finish a long rest.',
    prerequisites: [{ type: 'level', value: 9 }],
  },
  {
    id: 'mire-the-mind',
    name: 'Mire the Mind',
    description:
      'You can cast slow once using a warlock spell slot. You can\'t do so again until you finish a long rest.',
    prerequisites: [{ type: 'level', value: 5 }],
  },
  {
    id: 'misty-visions',
    name: 'Misty Visions',
    description:
      'You can cast silent image at will, without expending a spell slot or material components.',
  },
  {
    id: 'one-with-shadows',
    name: 'One with Shadows',
    description:
      'When you are in an area of dim light or darkness, you can use your action to become invisible until you move or take an action or a reaction.',
    prerequisites: [{ type: 'level', value: 5 }],
  },
  {
    id: 'otherworldly-leap',
    name: 'Otherworldly Leap',
    description:
      'You can cast jump on yourself at will, without expending a spell slot or material components.',
    prerequisites: [{ type: 'level', value: 9 }],
  },
  {
    id: 'repelling-blast',
    name: 'Repelling Blast',
    description:
      'When you hit a creature with eldritch blast, you can push the creature up to 10 feet away from you in a straight line.',
    prerequisites: [{ type: 'cantrip', value: 'eldritch-blast' }],
  },
  {
    id: 'sculptor-of-flesh',
    name: 'Sculptor of Flesh',
    description:
      'You can cast polymorph once using a warlock spell slot. You can\'t do so again until you finish a long rest.',
    prerequisites: [{ type: 'level', value: 7 }],
  },
  {
    id: 'sign-of-ill-omen',
    name: 'Sign of Ill Omen',
    description:
      'You can cast bestow curse once using a warlock spell slot. You can\'t do so again until you finish a long rest.',
    prerequisites: [{ type: 'level', value: 5 }],
  },
  {
    id: 'thief-of-five-fates',
    name: 'Thief of Five Fates',
    description:
      'You can cast bane once using a warlock spell slot. You can\'t do so again until you finish a long rest.',
  },
  {
    id: 'thirsting-blade',
    name: 'Thirsting Blade',
    description:
      'You can attack with your pact weapon twice, instead of once, whenever you take the Attack action on your turn.',
    prerequisites: [
      { type: 'level', value: 5 },
      { type: 'pact', value: 'pact-of-the-blade' },
    ],
  },
  {
    id: 'visions-of-distant-realms',
    name: 'Visions of Distant Realms',
    description: 'You can cast arcane eye at will, without expending a spell slot.',
    prerequisites: [{ type: 'level', value: 15 }],
  },
  {
    id: 'voice-of-the-chain-master',
    name: 'Voice of the Chain Master',
    description:
      'You can communicate telepathically with your familiar and perceive through your familiar\'s senses as long as you are on the same plane of existence. Additionally, while perceiving through your familiar\'s senses, you can also speak through your familiar in your own voice, even if your familiar is normally incapable of speech.',
    prerequisites: [{ type: 'pact', value: 'pact-of-the-chain' }],
  },
  {
    id: 'whispers-of-the-grave',
    name: 'Whispers of the Grave',
    description:
      'You can cast speak with dead at will, without expending a spell slot.',
    prerequisites: [{ type: 'level', value: 9 }],
  },
  {
    id: 'witch-sight',
    name: 'Witch Sight',
    description:
      'You can see the true form of any shapechanger or creature concealed by illusion or transmutation magic while the creature is within 30 feet of you and within line of sight.',
    prerequisites: [{ type: 'level', value: 15 }],
  },
];

/**
 * Levels at which Warlocks gain additional Eldritch Invocations
 * Level 2: 2 invocations
 * Level 5, 7, 9, 12, 15, 18: +1 invocation each
 */
export const INVOCATION_LEVELS = [2, 5, 7, 9, 12, 15, 18];

/**
 * Get number of invocations allowed for a given warlock level
 */
export function getInvocationCount(warlockLevel: number): number {
  if (warlockLevel < 2) return 0;
  let count = 2; // Start with 2 at level 2
  for (const level of INVOCATION_LEVELS.slice(1)) {
    // Skip first (level 2)
    if (warlockLevel >= level) count++;
  }
  return count;
}

/**
 * Check if a warlock meets prerequisites for an invocation
 */
export function meetsInvocationPrerequisites(
  invocation: EldritchInvocation,
  warlockLevel: number,
  pactBoonId: string | null,
  knownCantrips: string[]
): boolean {
  if (!invocation.prerequisites) return true;

  return invocation.prerequisites.every((prereq) => {
    switch (prereq.type) {
      case 'level':
        return warlockLevel >= (prereq.value as number);
      case 'pact':
        return pactBoonId === prereq.value;
      case 'cantrip':
        return knownCantrips.includes(prereq.value as string);
      case 'spell':
        // For now, assume spell prerequisites are met (would need to check known spells)
        return true;
      default:
        return true;
    }
  });
}

export function getEldritchInvocation(id: string): EldritchInvocation | undefined {
  return ELDRITCH_INVOCATIONS.find((inv) => inv.id === id);
}

export function getEldritchInvocationIds(): string[] {
  return ELDRITCH_INVOCATIONS.map((inv) => inv.id);
}

/**
 * Get available invocations for a warlock at a given level with their pact boon
 */
export function getAvailableInvocations(
  warlockLevel: number,
  pactBoonId: string | null,
  knownCantrips: string[]
): EldritchInvocation[] {
  return ELDRITCH_INVOCATIONS.filter((inv) =>
    meetsInvocationPrerequisites(inv, warlockLevel, pactBoonId, knownCantrips)
  );
}
